/**
 * How a note is encoded inside a comment on the host
 *
 * A note is an ordinary comment with a leading HTML comment carrying
 * whether the note is resolved and who wrote it - which can differ from
 * the account that posted it.
 * Hosts render HTML comments invisibly, so the marker stays out of the way
 * when the thread is read on the host's own site.
 *
 * Encoding is identical across backends
 */

const MARKER_PREFIX = '<!-- DecapCMS Note ';
const MARKER_SUFFIX = ' -->';
const NOTE_PATTERN = /^<!-- DecapCMS Note (\{[\s\S]*?\}) -->\n?([\s\S]*)$/;
const LEGACY_NOTE_PATTERN = /^<!-- DecapCMS Note - Status: (RESOLVED|OPEN) -->\n?([\s\S]*)$/;

/**
 * A JSON string may contain `-->`, which would close the HTML comment early.
 * Escaping every hyphen that precedes another removes every `--`, so `-->`
 * cannot survive; `JSON.parse` decodes `\u002d` back, so it stays lossless
 * and a lone hyphen (`Jean-Luc`) stays readable.
 *
 * Replacing `--` instead is not enough - a run of five hyphens leaves one
 * behind at the seam.
 */
function encode(payload) {
  return JSON.stringify(payload).replace(/-(?=-)/g, '\\u002d');
}
export function formatNoteBody(note) {
  // Both or neither: a name with no id to compare against buys nothing, and a
  // backend whose poster IS the editor records neither.
  const identity = note.authorId && note.author ? {
    author: note.author,
    authorId: note.authorId
  } : {};
  const marker = encode({
    resolved: note.resolved,
    ...identity
  });
  return `${MARKER_PREFIX}${marker}${MARKER_SUFFIX}\n${note.content}`;
}

/**
 * The marker is hand-editable on the host, so nothing in it is trusted to be
 * the type it should be. A non-string author reaching `Note` crashes the pane
 * outright - the avatar initials call `.split()` on it - so anything that is
 * not a usable string is treated as no recorded author, which falls back to
 * the account that posted the comment.
 */
function asString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
export function parseNoteBody(body) {
  const match = body.match(NOTE_PATTERN);
  if (!match) {
    const legacy = body.match(LEGACY_NOTE_PATTERN);
    if (legacy) {
      return {
        content: legacy[2].trim(),
        resolved: legacy[1] === 'RESOLVED'
      };
    }

    // A comment typed straight into the thread on the host. It is still a note,
    // just an unresolved one with no recorded author.
    return {
      content: body.trim(),
      resolved: false
    };
  }
  let payload;
  try {
    payload = JSON.parse(match[1]);
  } catch {
    // Someone edited the marker by hand into something unparseable. Treat the
    // whole comment as content rather than dropping the note.
    return {
      content: body.trim(),
      resolved: false
    };
  }
  const author = asString(payload.author);
  const authorId = asString(payload.authorId);

  // Both or neither, as formatNoteBody writes them.
  const identity = author && authorId ? {
    author,
    authorId
  } : {};
  return {
    content: match[2].trim(),
    resolved: payload.resolved === true,
    author: identity.author,
    authorId: identity.authorId
  };
}
export function commentToNote(comment) {
  if (!comment || !comment.body) {
    throw new Error('Invalid comment structure');
  }
  const {
    content,
    resolved,
    author,
    authorId
  } = parseNoteBody(comment.body);
  if (!content) {
    throw new Error('Empty note content');
  }
  return {
    id: comment.id.toString(),
    // Falls back to the account that posted, for a comment typed on the host
    // and for any note with no recorded author.
    author: author || comment.user?.login || 'Unknown',
    authorId,
    // Only when the posting account IS the note's author — a recorded author
    // means someone posted on their behalf, and its avatar would mislabel the
    // note. The pane shows initials instead.
    avatarUrl: authorId ? undefined : comment.user?.avatar_url || undefined,
    timestamp: comment.created_at,
    content,
    resolved,
    entrySlug: ''
  };
}
export function commentsToNotes(comments, issueUrl, toNote = commentToNote) {
  return comments.reduce((notes, comment) => {
    try {
      notes.push({
        ...toNote(comment),
        issueUrl
      });
    } catch (error) {
      // Not a note; skip it
    }
    return notes;
  }, []);
}
export function markOwnNotes(notes, identity) {
  return notes.map(note => ({
    ...note,
    isOwn: note.authorId ? note.authorId === identity.authorId : note.author === identity.author
  }));
}